import { IChannelAvailability, ISection, IVenueAvailability } from 'src/types/menu.types';

type TIds = string[] | null | undefined;

export const isRestricted = (ids: TIds): boolean => Array.isArray(ids);

export const getEffectiveIds = (ownIds: TIds, parentIds: TIds): string[] | null => {
  if (!isRestricted(ownIds)) return isRestricted(parentIds) ? (parentIds as string[]) : null;
  if (!isRestricted(parentIds)) return ownIds as string[];
  return (ownIds as string[]).filter((id) => (parentIds as string[]).includes(id));
};

type TSectionAvailability = Pick<ISection, 'instanceIds'>;
type TSectionAvailabilityWithChannels = Pick<ISection, 'instanceIds' | 'channels'>;

type TSectionVenueAvailability = Pick<ISection, 'venueIds'>;
type TSectionVenueAvailabilityWithVenues = Pick<ISection, 'venueIds' | 'venues'>;

export const isSectionRestricted = (section: TSectionAvailability): boolean => isRestricted(section.instanceIds);

export const getEffectiveInstanceIds = (
  section: TSectionAvailability,
  parentSection?: TSectionAvailability,
): string[] | null => getEffectiveIds(section.instanceIds, parentSection?.instanceIds);

export const hasNoAvailability = (section: TSectionAvailability, parentSection?: TSectionAvailability): boolean =>
  isSectionRestricted(section) && getEffectiveInstanceIds(section, parentSection)?.length === 0;

export const getEffectiveChannels = (
  section: TSectionAvailabilityWithChannels,
  parentSection?: TSectionAvailabilityWithChannels,
): IChannelAvailability[] => {
  if (!isSectionRestricted(section)) return [];
  const effectiveInstanceIds = getEffectiveInstanceIds(section, parentSection) ?? [];
  return (section.channels ?? []).filter((channel) => effectiveInstanceIds.includes(channel.externalSystemId));
};

export interface IResolvedAvailability {
  instanceIds: string[] | null;
  channels: IChannelAvailability[];
}

/**
 * A section's own effective channel availability, resolved by walking the whole ancestor
 * chain (not just the immediate parent) — a subsection that itself has no restriction
 * inherits whatever its parent resolves to, which may in turn be inherited from a
 * grandparent, and so on.
 */
export const resolveSectionAvailability = (section: ISection, sections: ISection[]): IResolvedAvailability => {
  const parent = section.parentSectionId
    ? sections.find((candidate) => candidate.sectionId === section.parentSectionId)
    : undefined;
  const parentResolved: IResolvedAvailability = parent
    ? resolveSectionAvailability(parent, sections)
    : { instanceIds: null, channels: [] };

  if (!isSectionRestricted(section)) return parentResolved;

  return {
    instanceIds: getEffectiveInstanceIds(section, { instanceIds: parentResolved.instanceIds }) ?? [],
    channels: getEffectiveChannels(section, parentResolved),
  };
};

/** True when any section, once its ancestors are resolved, leaves out one of the selected instances. */
export const hasPartialAvailability = (sections: ISection[], selectedInstanceIds: string[]): boolean =>
  sections.some((section) => {
    const { instanceIds } = resolveSectionAvailability(section, sections);
    return instanceIds !== null && selectedInstanceIds.some((id) => !instanceIds.includes(id));
  });

export const isSectionVenueRestricted = (section: TSectionVenueAvailability): boolean => isRestricted(section.venueIds);

export const getEffectiveVenueIds = (
  section: TSectionVenueAvailability,
  parentSection?: TSectionVenueAvailability,
): string[] | null => getEffectiveIds(section.venueIds, parentSection?.venueIds);

export const hasNoVenueAvailability = (
  section: TSectionVenueAvailability,
  parentSection?: TSectionVenueAvailability,
): boolean => isSectionVenueRestricted(section) && getEffectiveVenueIds(section, parentSection)?.length === 0;

export const getEffectiveVenues = (
  section: TSectionVenueAvailabilityWithVenues,
  parentSection?: TSectionVenueAvailabilityWithVenues,
): IVenueAvailability[] => {
  if (!isSectionVenueRestricted(section)) return [];
  const effectiveVenueIds = getEffectiveVenueIds(section, parentSection) ?? [];
  return (section.venues ?? []).filter((venue) => effectiveVenueIds.includes(venue.venueId));
};

export interface IResolvedVenueAvailability {
  venueIds: string[] | null;
  venues: IVenueAvailability[];
}

/**
 * The venue-axis equivalent of resolveSectionAvailability — same ancestor-chain walk,
 * applied to venueIds/venues instead of instanceIds/channels.
 */
export const resolveSectionVenueAvailability = (
  section: ISection,
  sections: ISection[],
): IResolvedVenueAvailability => {
  const parent = section.parentSectionId
    ? sections.find((candidate) => candidate.sectionId === section.parentSectionId)
    : undefined;
  const parentResolved: IResolvedVenueAvailability = parent
    ? resolveSectionVenueAvailability(parent, sections)
    : { venueIds: null, venues: [] };

  if (!isSectionVenueRestricted(section)) return parentResolved;

  return {
    venueIds: getEffectiveVenueIds(section, { venueIds: parentResolved.venueIds }) ?? [],
    venues: getEffectiveVenues(section, parentResolved),
  };
};

export interface ISectionUnavailability {
  isChannelsUnavailable: boolean;
  isVenuesUnavailable: boolean;
  isSectionUnavailable: boolean;
}

const isEmptyRestriction = (ids: string[] | null): boolean => ids !== null && ids.length === 0;

export const getSectionUnavailability = (section: ISection, sections: ISection[]): ISectionUnavailability => {
  const isChannelsUnavailable = isEmptyRestriction(resolveSectionAvailability(section, sections).instanceIds);
  const isVenuesUnavailable = isEmptyRestriction(resolveSectionVenueAvailability(section, sections).venueIds);
  return {
    isChannelsUnavailable,
    isVenuesUnavailable,
    isSectionUnavailable: isChannelsUnavailable || isVenuesUnavailable,
  };
};

export interface ISectionRestrictions {
  isRestricted: boolean;
  channels: IChannelAvailability[];
  venueCount: number | undefined;
}

export const getSectionRestrictions = (section: ISection): ISectionRestrictions => {
  const isChannelRestricted = isSectionRestricted(section);
  const isVenueRestricted = isSectionVenueRestricted(section);
  return {
    isRestricted: isChannelRestricted || isVenueRestricted,
    channels: isChannelRestricted ? section.channels ?? [] : [],
    venueCount: isVenueRestricted ? (section.venues ?? []).length : undefined,
  };
};
